import { FC, ReactElement, useEffect, useRef } from 'react';

import { useTechnical } from '@lib/core/service/hooks/useTechnical';
import { scanCode } from '@lib/core/service/slices/technical/scanner';
import { useFidelityCard } from '@lib/core/users/hooks/useFidelityCard';
import { closeFidelityCardModal, firstTimeUserSimulationScan } from '@lib/core/users/slices/fidelityCard';
import { localeKiosk } from '@lib/tools/locale/source/web/kiosk';
import { ILocaleText } from '@lib/tools/locale/views/LocaleFragment';
import { FidelityModalsTypes } from '@lib/tools/shared/helpers/consts';
import { useAddons, useDisclosure, useTypedDispatch } from '@lib/tools/views/hooks';

import {
  LOADING_SPINNER_VARIANTS,
  LoadingSpinner,
} from '@components/web/src/templates/Loading/LoadingSpinner/LoadingSpinner';
import {
  ScanResultModal,
  ScanResultModalIconType,
} from '@components/web/src/templates/Modals/ScanResultModal/ScanResultModal';

type IProps = {
  children: ReactElement;
};

const ScannerHOC: FC<IProps> = ({ children }) => {
  const dispatch = useTypedDispatch();

  const { isTabletKioskScannerAddon } = useAddons();
  const scanOnCooldown = useRef(false);
  const scanCooldownTimer = useRef(null);
  const { activeFidelityModalCode } = useFidelityCard();
  const [isOpen, { open, close }] = useDisclosure({ defaultIsOpen: false });
  const { isScannerLoading } = useTechnical();

  const {
    notCardOrEan,
    foundFidelityCardTitle,
    foundFidelityCardSubtitle,
    alreadyAssociatedFidelityCard,
    disconnectedFidelityCardTitle,
    disconnectedFidelityCardSubtitle,
    serviceUnavailable,
    fidelityCardTitle,
  } = localeKiosk.fidelityCard;

  const fidelityModalsData: Record<
    FidelityModalsTypes,
    {
      subtitle: ILocaleText;
      icon: ScanResultModalIconType;
      title: ILocaleText;
      withButton: boolean;
    }
  > = {
    [FidelityModalsTypes.SuccessWithoutCharacter]: {
      icon: 'Occasion',
      subtitle: foundFidelityCardSubtitle,
      title: foundFidelityCardTitle,
      withButton: false,
    },
    [FidelityModalsTypes.SuccessWithCharacter]: {
      icon: 'Occasion',
      subtitle: alreadyAssociatedFidelityCard,
      title: '',
      withButton: false,
    },
    [FidelityModalsTypes.CardDisconnected]: {
      icon: 'Curious',
      subtitle: disconnectedFidelityCardTitle,
      title: disconnectedFidelityCardSubtitle,
      withButton: true,
    },
    [FidelityModalsTypes.NotCardOrEan]: {
      icon: 'Question',
      subtitle: notCardOrEan,
      title: fidelityCardTitle,
      withButton: true,
    },
    [FidelityModalsTypes.NotFoundCard]: {
      icon: 'FidelityCard',
      subtitle: notCardOrEan,
      title: fidelityCardTitle,
      withButton: true,
    },
    [FidelityModalsTypes.ServiceUnavailable]: {
      icon: 'FidelityCard',
      subtitle: serviceUnavailable,
      title: fidelityCardTitle,
      withButton: true,
    },
  };

  const modalData = fidelityModalsData[activeFidelityModalCode];

  let eanCode = '';

  const scannerListener = (e: KeyboardEvent) => {
    /**
     * Conditions for a valid EAN:
     * 1. Must consist of numbers
     * UPDATED: now it can be a string of numbers
     * 2. Must be between 6 and 13 characters
     * 3. Scan ends with an 'Enter' keystroke.
     * 4. Target input mustn't be a field
     */

    const textKey = e.key || String.fromCharCode(e.keyCode);

    const { target } = e;
    const textTarget = (target as HTMLElement)?.tagName;

    const resetScan = () => {
      eanCode = '';
      scanOnCooldown.current = true;
      scanCooldownTimer.current = setTimeout(() => {
        scanOnCooldown.current = false;
      }, 2000);
    };

    const isTestFirstTimeUserFidelityCard = eanCode === '421034841177';

    if (textTarget !== 'INPUT') {
      if (textKey.length === 1) {
        eanCode += textKey;
      } else if (eanCode.length >= 6 && eanCode.length <= 13 && textKey === 'Enter') {
        if (isTestFirstTimeUserFidelityCard) {
          /**
           * QA Simulation first-time user session without:
           * 1. Saving characters to the card
           * 2. Making any backend requests for the card
           */
          dispatch(firstTimeUserSimulationScan(String(eanCode)));
          resetScan();
        } else if (!isScannerLoading && !scanOnCooldown.current) {
          dispatch(scanCode(String(eanCode)));
          resetScan();
        }
      } else {
        eanCode = '';
      }
    }
  };

  const handleHideModal = () => {
    dispatch(closeFidelityCardModal());
    close();
  };

  useEffect(() => {
    let timeoutId;

    if (isOpen) {
      timeoutId = setTimeout(() => {
        handleHideModal();
      }, 5000);
    }

    return () => clearTimeout(timeoutId);
  }, [isOpen, close]);

  useEffect(() => {
    if (activeFidelityModalCode) {
      open();
    }
  }, [activeFidelityModalCode]);

  useEffect(() => {
    /**
     * Tablet kiosk uses QrScanner (camera), no keyboard listener needed
     */
    if (isTabletKioskScannerAddon) return () => {};

    /**
     * Physical scanner acts as a keyboard (digits + Enter)
     *
     * When the App container is re-rendered, this hoc is re-mounted.
     * Ensure there's only one keydown listener applied.
     */
    document.addEventListener('keydown', scannerListener);

    return () => {
      if (!isTabletKioskScannerAddon) {
        document.removeEventListener('keydown', scannerListener);
        clearTimeout(scanCooldownTimer.current);
      }
    };
  }, []);

  return (
    <>
      {isScannerLoading ? (
        <LoadingSpinner variant={LOADING_SPINNER_VARIANTS.SCANNER} />
      ) : (
        <>
          <ScanResultModal
            hideModal={handleHideModal}
            icon={modalData?.icon}
            isOpen={isOpen}
            subtitle={modalData?.subtitle || ''}
            title={modalData?.title || ''}
            withButton={modalData?.withButton}
          />
          {children}
        </>
      )}
    </>
  );
};

export default ScannerHOC;
